import { Box } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import type { DraggableBounds, DraggableEvent } from 'react-draggable'
import Draggable from 'react-draggable'
import { useDispatch } from 'react-redux'

import { useAppSelector } from '@/hooks/redux'
import { setDraggableContainerMountRef } from '@/store/sessionSlice'

interface Position {
  x: number
  y: number
}

interface DraggableContainerProps {
  children: React.ReactNode
  axis?: 'x' | 'y' | 'both'
  bounds?: string | DraggableBounds
  className?: string
  style?: React.CSSProperties
  onDragEnd?: (event: DraggableEvent) => void
  defaultPosition: Position
  styleContainer?: React.CSSProperties
}

const DraggableContainer: React.FC<DraggableContainerProps> = ({
  children,
  axis = 'both',
  bounds,
  defaultPosition,
  styleContainer,
  ...rest
}) => {
  const nodeRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const draggableContainerMountRef = useAppSelector(
    (state) => state.session.draggableContainerMountRef,
  )
  const defaultPositionRef = useRef<Position>(defaultPosition)

  useEffect(() => {
    if (!defaultPositionRef.current.x || !defaultPositionRef.current.y) {
      defaultPositionRef.current = defaultPosition
    }
  }, [defaultPosition])

  const [position, setPosition] = useState<Position>(defaultPosition)

  useEffect(() => {
    if (
      (!position?.x ||
        !position?.y ||
        defaultPositionRef.current.x !== defaultPosition.x ||
        defaultPositionRef.current.y !== defaultPosition.y) &&
      !draggableContainerMountRef
    ) {
      setPosition(defaultPosition)
      defaultPositionRef.current = defaultPosition
    }
  }, [defaultPosition, position, draggableContainerMountRef])

  const handleDrag = (_e: DraggableEvent, pos: Position) => {
    let x = defaultPosition.x
    let y = defaultPosition.y

    if (axis === 'both') {
      x = pos.x
      y = pos.y
    } else if (axis === 'y') {
      y = pos.y
    } else if (axis === 'x') {
      x = pos.x
    }

    setPosition({ x, y })
    dispatch(setDraggableContainerMountRef(true))
  }

  return (
    <Draggable
      axis={axis}
      bounds={bounds}
      grid={[1, 1]}
      scale={1}
      position={position}
      nodeRef={nodeRef as React.RefObject<HTMLElement>}
      onDrag={handleDrag}
      {...rest}
    >
      <Box
        ref={nodeRef}
        position="absolute"
        sx={{
          cursor: 'move',
          userSelect: 'none',
          touchAction: 'none',
          ...styleContainer,
        }}
      >
        {children}
      </Box>
    </Draggable>
  )
}

export default DraggableContainer
